import React, { createContext, useContext, useState } from 'react';
import supabase from '../lib/supabase';
import { useAuth } from './AuthContext';

const RepresentativeContext = createContext();

export const useRepresentatives = () => {
  const context = useContext(RepresentativeContext);
  if (!context) {
    throw new Error('useRepresentatives must be used within a RepresentativeProvider');
  }
  return context;
};

export const RepresentativeProvider = ({ children }) => {
  const { user } = useAuth();
  const [representatives, setRepresentatives] = useState({
    federal: [],
    state: [],
    local: []
  });
  const [districts, setDistricts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationRequests, setVerificationRequests] = useState([]);

  // Mock verification requests for admin UI
  const mockVerificationRequests = [
    {
      id: 101,
      representativeId: 3,
      name: 'Lizzie Fletcher',
      position: 'U.S. Representative',
      status: 'pending',
      requestDate: '2023-11-15'
    },
    {
      id: 102,
      representativeId: 5,
      name: 'Sarah Davis',
      position: 'State Representative',
      status: 'pending',
      requestDate: '2023-12-03'
    }
  ];

  // Load verification requests for admin users
  React.useEffect(() => {
    if (user?.role === 'admin') {
      setVerificationRequests(mockVerificationRequests);
    }
  }, [user]);

  // Fetch representatives using Google Civic Information API
  const fetchRepresentatives = async (address) => {
    setLoading(true);
    setError(null);

    try {
      // In a real implementation, this would call the Google Civic API
      // For demo purposes, we'll use mock data first, then implement the API call

      // Construct the address string
      const addressStr = address.fullAddress || `${address.street}, ${address.city}, ${address.state} ${address.zipCode}`;
      if (!addressStr) {
        throw new Error("Please provide a valid address");
      }

      // Call the Google Civic API (simulated for now)
      const CIVIC_API_KEY = 'AIzaSyC86MKVZQRT_PWcrEx3IJU418vo10I5iSs';
      const url = `https://civicinfo.googleapis.com/civicinfo/v2/representatives?key=${CIVIC_API_KEY}&address=${encodeURIComponent(addressStr)}`;
      
      try {
        // Actual API call (commented out for demo)
        // const response = await fetch(url);
        // const data = await response.json();
        
        // If API call fails or for demo, use mock data
        
        // Process and categorize the representatives
        const processedData = processMockCivicData(addressStr);
        
        // Update districts
        setDistricts({
          congressional: processedData.districts.congressional || 'Unknown',
          stateSenate: processedData.districts.stateSenate || 'Unknown',
          stateHouse: processedData.districts.stateHouse || 'Unknown',
          county: processedData.districts.county || 'Unknown',
          city: processedData.districts.city || 'Unknown'
        });
        
        // Update representatives
        setRepresentatives({
          federal: processedData.federal || [],
          state: processedData.state || [],
          local: processedData.local || []
        });
      } catch (apiError) {
        console.error('API Error:', apiError);
        
        // Fallback to mock data
        const mockData = processMockCivicData(addressStr);
        
        setDistricts({
          congressional: mockData.districts.congressional,
          stateSenate: mockData.districts.stateSenate,
          stateHouse: mockData.districts.stateHouse,
          county: mockData.districts.county,
          city: mockData.districts.city
        });
        
        setRepresentatives({
          federal: mockData.federal,
          state: mockData.state,
          local: mockData.local
        });
      }
    } catch (err) {
      console.error('Error fetching representatives:', err);
      setError(err.message || "Failed to load your representatives. Please try again.");
      
      // Clear previous data on error
      setDistricts(null);
      setRepresentatives({
        federal: [],
        state: [],
        local: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Process mock civic data (simulating API response)
  const processMockCivicData = (addressStr) => {
    // Extract state from address for basic customization
    const stateMatch = addressStr.match(/([A-Z]{2})\s*\d{5}/) || addressStr.match(/\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)\b/);
    const state = stateMatch ? stateMatch[1] : 'TX'; // Default to TX if no state found
    
    // City extraction (simple pattern)
    const cityMatch = addressStr.match(/([A-Za-z\s]+),\s*[A-Z]{2}/);
    const city = cityMatch ? cityMatch[1].trim() : 'Houston';
    
    // Generate mock data based on the address
    const mockData = {
      districts: {
        congressional: `${state === 'TX' ? '7' : '1'}th Congressional District`,
        stateSenate: `State Senate District ${state === 'TX' ? '14' : '1'}`,
        stateHouse: `State House District ${state === 'TX' ? '45' : '1'}`,
        county: `${state === 'TX' ? 'Harris' : 'Washington'} County`,
        city: city
      },
      federal: [
        {
          id: 1,
          name: state === 'TX' ? 'Ted Cruz' : 'Generic Senator',
          position: 'U.S. Senator',
          party: 'Republican',
          photo: 'https://www.cruz.senate.gov/wp-content/uploads/2022/08/TedCruz-Headshot-2022-scaled-e1660834963141.jpg',
          office: {
            phone: '(202) 224-5922',
            email: 'contact@cruz.senate.gov',
            website: 'https://cruz.senate.gov',
            address: '127A Russell Senate Office Building, Washington, DC 20510'
          },
          socialMedia: {
            twitter: '@SenTedCruz',
            facebook: 'SenatorTedCruz'
          },
          verified: true,
          localOffices: [
            {
              city: state === 'TX' ? 'Houston' : city,
              phone: '(713) 718-3057',
              address: '808 Travis Street, Suite 1420, Houston, TX 77002'
            }
          ]
        },
        {
          id: 2,
          name: state === 'TX' ? 'John Cornyn' : 'Generic Senator 2',
          position: 'U.S. Senator',
          party: 'Republican',
          photo: 'https://www.cornyn.senate.gov/wp-content/uploads/2022/08/Cornyn-Official-116th-Congress-Photo.jpg',
          office: {
            phone: '(202) 224-2934',
            email: 'contact@cornyn.senate.gov',
            website: 'https://cornyn.senate.gov',
            address: '517 Hart Senate Office Building, Washington, DC 20510'
          },
          socialMedia: {
            twitter: '@JohnCornyn',
            facebook: 'SenJohnCornyn'
          },
          verified: true,
          localOffices: [
            {
              city: state === 'TX' ? 'Houston' : city,
              phone: '(713) 572-3337',
              address: '221 W. 6th Street, Suite 1530, Austin, TX 78701'
            }
          ]
        },
        {
          id: 3,
          name: state === 'TX' ? 'Lizzie Fletcher' : `Representative for ${city}`,
          position: 'U.S. Representative',
          district: state === 'TX' ? '7th Congressional District' : '1st Congressional District',
          party: 'Democrat',
          photo: 'https://fletcher.house.gov/sites/evo-subsites/fletcher.house.gov/files/evo-media-image/Official%20Photo%20-%20117th%20Congress.jpg',
          office: {
            phone: '(202) 225-2571',
            email: 'contact@fletcher.house.gov',
            website: 'https://fletcher.house.gov',
            address: '1429 Longworth House Office Building, Washington, DC 20515'
          },
          socialMedia: {
            twitter: '@RepFletcher',
            facebook: 'RepFletcher'
          },
          verified: false,
          localOffices: [
            {
              city: state === 'TX' ? 'Houston' : city,
              phone: '(713) 353-8680',
              address: '5599 San Felipe Street, Suite 550, Houston, TX 77056'
            }
          ]
        }
      ],
      state: [
        {
          id: 4,
          name: state === 'TX' ? 'Paul Bettencourt' : `State Senator for ${city}`,
          position: 'State Senator',
          district: state === 'TX' ? 'District 7' : 'District 1',
          party: 'Republican',
          photo: 'https://senate.texas.gov/Images/Members/Headshots/Bettencourt_Paul_87.jpg',
          office: {
            phone: '(512) 463-0107',
            email: `paul.bettencourt@senate.${state.toLowerCase()}.gov`,
            website: `https://senate.${state.toLowerCase()}.gov/member.php?d=7`,
            address: 'Capitol Office: Room 3E.20, Austin, TX 78701'
          },
          socialMedia: {
            twitter: '@TeamBettencourt',
            facebook: 'SenatorPaulBettencourt'
          },
          verified: true,
          localOffices: [
            {
              city: state === 'TX' ? 'Houston' : city,
              phone: '(713) 464-0282',
              address: '10077 Grogans Mill Road, Suite 125, The Woodlands, TX 77380'
            }
          ]
        },
        {
          id: 5,
          name: state === 'TX' ? 'Sarah Davis' : `State Representative for ${city}`,
          position: 'State Representative',
          district: state === 'TX' ? 'District 134' : 'District 1',
          party: 'Republican',
          photo: 'https://tlc.texas.gov/Legislators/Members/MemberPictures/87/Davis_Sarah.jpg',
          office: {
            phone: '(512) 463-0389',
            email: `sarah.davis@house.${state.toLowerCase()}.gov`,
            website: `https://house.${state.toLowerCase()}.gov/members/member-page/?district=134`,
            address: 'Capitol Office: Room 2W.11, Austin, TX 78701'
          },
          socialMedia: {
            twitter: '@SarahforHD134',
            facebook: 'SarahforHD134'
          },
          verified: false,
          localOffices: [
            {
              city: state === 'TX' ? 'Houston' : city,
              phone: '(713) 521-4474',
              address: '6300 West Loop South, Suite 140, Houston, TX 77401'
            }
          ]
        }
      ],
      local: [
        {
          id: 6,
          name: state === 'TX' ? 'Lina Hidalgo' : `County Judge for ${city}`,
          position: 'County Judge',
          jurisdiction: state === 'TX' ? 'Harris County' : `${city} County`,
          party: 'Democrat',
          photo: 'https://www.harriscountytx.gov/portals/0/headshots/judge.jpg',
          office: {
            phone: '(713) 755-5113',
            email: 'cjo@hctx.net',
            website: 'https://cjo.hctx.net',
            address: '1001 Preston Street, Suite 911, Houston, TX 77002'
          },
          socialMedia: {
            twitter: '@LinaHidalgoTX',
            facebook: 'LinaHidalgoTX'
          },
          verified: false
        },
        {
          id: 7,
          name: state === 'TX' ? 'Sylvester Turner' : `Mayor of ${city}`,
          position: 'Mayor',
          jurisdiction: city,
          party: 'Democrat',
          photo: 'https://www.houstontx.gov/mayor/images/mayor-portrait-2020.jpg',
          office: {
            phone: '(713) 837-0311',
            email: `mayor@${city.toLowerCase().replace(' ', '')}tx.gov`,
            website: `https://www.${city.toLowerCase().replace(' ', '')}tx.gov/mayor/`,
            address: `City Hall, 901 Bagby Street, ${city}, ${state} 77002`
          },
          socialMedia: {
            twitter: '@SylvesterTurner',
            facebook: 'SylvesterTurner'
          },
          verified: false
        }
      ]
    };

    return mockData;
  };

  // Submit verification request
  const submitVerificationRequest = async (representativeId, userData) => {
    try {
      // In real implementation, insert into Supabase
      console.log(`Submitting verification request for representative ID: ${representativeId}`, userData);
      return { success: true };
    } catch (err) {
      console.error('Error submitting verification request:', err);
      return { error: 'Failed to submit verification request' };
    }
  };

  // Handle verification request approval
  const approveVerificationRequest = async (requestId) => {
    try {
      // In real implementation, update in Supabase
      setVerificationRequests(verificationRequests.filter(req => req.id !== requestId));
      return { success: true };
    } catch (err) {
      console.error('Error approving verification request:', err);
      return { error: 'Failed to approve verification request' };
    }
  };

  // Handle verification request denial
  const denyVerificationRequest = async (requestId) => {
    try {
      // In real implementation, update in Supabase
      setVerificationRequests(verificationRequests.filter(req => req.id !== requestId));
      return { success: true };
    } catch (err) {
      console.error('Error denying verification request:', err);
      return { error: 'Failed to deny verification request' };
    }
  };

  const value = {
    representatives,
    districts,
    loading,
    error,
    verificationRequests,
    fetchRepresentatives,
    submitVerificationRequest,
    approveVerificationRequest,
    denyVerificationRequest
  };

  return (
    <RepresentativeContext.Provider value={value}>
      {children}
    </RepresentativeContext.Provider>
  );
};